import React, { useEffect, useRef, useState } from 'react'
import Text from '../../../../components/Text'
import BottomSheet from '../../../../components/BottomSheet'
import useDoctorsLocation from '../../hooks/useDoctorsLocation'
import Spinner from '../../../../components/Spinner'
import ChevronDownIcon from '../../../../components/Icons/ChevronRight'
import SearchIcon from '../../../../components/Icons/SearchIcon'
import ProceedButton from '../../../../components/Signup/ProceedButton'
import InputField from '../../../../components/InputField'
import { supportProgramTypes } from '../../../../constants/support-programs'
import constants from '../../../../constants'
import { showErrorToast } from '../../../../utils/toast'
import * as actions from '../../../../store/slices'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'
import { updatePatientDoctor } from '../../../../api'
import InfiniteScrollPagination from '../../../../components/Pagination/InfiniteScrollPagination'
import { trackEvent } from '../../../../analytics'
import { EVENTS } from '../../../../analytics/events'

const DoctorLocation = ({
  value,
  onChange,
  formObj,
  onMoveNext,
  isLastStep,
  canSkip,
  onSkip,
  pageObj,
  initialSelection,
}) => {
  const dispatch = useDispatch()
  const stateScrollId = 'doctor-location-state-scroll'
  const stateScrollRef = useRef(null)
  const {
    list,
    selectedState,
    selectedCity,
    activeSection,
    handleOnCloseSheet,
    searchQuery,
    handleOnSearch,
    handleOptionClick,
    handleActiveSession,
    loading,
    selectedDoctor,
    selectedHospital,
    doctorName,
    addDoctor,
    setDoctorName,
    setAddDoctor,
    handleAddDoctor,
    addDoctorLoading,
    hospitalName,
    setHospitalName,
    addHospital,
    setAddHospital,
    handleAddHospital,
    addHospitalLoading,
    loadMoreCities,
    hasMoreCities,
    loadMoreHospitals,
    hasMoreHospitals,
    loadMoreDoctors,
    hasMoreDoctors,
    hasMoreStates,
    loadMoreStates,
    isCgmFlow,
    cgmType,
    isDiasensCGM,
  } = useDoctorsLocation({
    onChange,
    source: pageObj?.source,
    initialSelection,
  })
  const { verifyOtp } = useSelector((s) => s.auth)
  const authData = verifyOtp?.data?.data || {}
  const [processing, setProcessing] = useState(false)

  // If container isn't scrollable on tall screens, keep fetching next state pages
  // until scrolling becomes possible (or no more records are available).
  useEffect(() => {
    if (activeSection !== 'state' || !hasMoreStates || loading) return
    const container = stateScrollRef.current
    if (!container) return

    const isScrollable = container.scrollHeight > container.clientHeight
    if (!isScrollable) {
      loadMoreStates()
    }
  }, [activeSection, list?.length, hasMoreStates, loading, loadMoreStates])

  // Fallback for city/hospital/doctor pagination: near-bottom scroll loads next page.
  const handleListScroll = (event) => {
    const isCity = activeSection === 'city'
    const isHospital = activeSection === 'hospital'
    const isDoctor = activeSection === 'doctor'
    if (!isCity && !isHospital && !isDoctor) return

    if (
      (isCity && !hasMoreCities) ||
      (isHospital && !hasMoreHospitals) ||
      (isDoctor && !hasMoreDoctors) ||
      loading
    )
      return

    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100

    if (isNearBottom) {
      if (isCity) {
        loadMoreCities()
      } else if (isHospital) {
        loadMoreHospitals()
      } else {
        loadMoreDoctors()
      }
    }
  }

  const handleFinish = () => {
    const phoneNumber =
      localStorage.getItem('phone_number') || authData?.contact_no
    setProcessing(true)
    const payload = {
      payload: {
        ...formObj,
        email: '',
        password: '',
        profile_pic: '',
        conf_password: '',
        whatsapp_optin: '',
        email_verified: '',
        active_deactive_id: '',
        is_accept_terms_accept: '',
        register: true,
        dob: formObj?.dob
          ? moment(formObj.dob).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
          : undefined,
        contact_no: phoneNumber,
        access_code: selectedDoctor?.access_code,
        account_role: 'P',
        // Only include patient_meta_info for PSP flow, not for CGM flow
        ...(!isCgmFlow && {
          patient_meta_info: {
            insurance_taken: '',
            annual_income: '',
            doctor_name: selectedDoctor?.name,
            user_type: 'Patient',
            support_program_type:
              supportProgramTypes[constants.CARE_PROGRAM_TYPE.ANAYA]?.name,
          },
        }),
      },
      headers: {
        // Use CGM_USER_FROM for CGM flow, PSP_USER_FROM for PSP flow
        userFrom: isCgmFlow ? constants.CGM_USER_FROM : constants.PSP_USER_FROM,
        source: constants.GOOD_FLIP_SOURCE,
      },
    }
    trackEvent(EVENTS.MY_SAATHI_DOCTOR_DETAILS_CONTINUE_CLICKED, {
      screenName: 'Onboarding Screen',
      doctorDetails: JSON.stringify({
        selectedDoctor,
        selectedHospital,
        selectedCity,
      }),
    })

    if (pageObj?.withoutAPI) {
      const payloadObj = {
        selectedState,
        selectedDoctor,
        selectedHospital,
        selectedCity,
      }
      onMoveNext(true, payloadObj)
    } else {
      new Promise((resolve, reject) => {
        dispatch(
          actions.registerPatientRequest({
            payload,
            resolve,
            reject,
          }),
        )
      })
        .then(async (res) => {
          if (Number(res?.code || 0) === 1) {
            localStorage.setItem('auth_token', res?.data?.token)
            dispatch(actions.updatePatientDetailsToken(res?.data?.token))

            // Call update_patient_doctor API for diasensCGM flow
            if (isDiasensCGM && selectedDoctor && selectedHospital) {
              try {
                console.log('Updating patient doctor for diasensCGM', {
                  patientId: res?.data?.patient_id,
                  selectedDoctor,
                  selectedHospital,
                })

                const updateDoctorPayload = {
                  patientId: res?.data?.patient_id,
                  newDoctorId:
                    selectedDoctor?.id ||
                    selectedDoctor?.doctor_id ||
                    selectedDoctor?.dr_id,
                  newDoctorClinicId:
                    selectedHospital?.clinic_master_id ||
                    selectedHospital?.clinic_id,
                  newDoctorClinicAddress:
                    selectedHospital?.address ||
                    selectedHospital?.clinic_address ||
                    selectedHospital?.clinic_name,
                }

                const updateDoctorResponse = await updatePatientDoctor(
                  updateDoctorPayload,
                  res?.data?.token,
                )

                if (Number(updateDoctorResponse?.code || 0) !== 1) {
                  console.warn(
                    'Failed to update patient doctor:',
                    updateDoctorResponse?.message,
                  )
                  // Don't block the flow if this fails, just log it
                } else {
                  console.log('Successfully updated patient doctor')
                }
              } catch (error) {
                console.error('Error updating patient doctor:', error)
                // Don't block the flow if this fails
              }
            }

            onMoveNext()
          } else {
            showErrorToast(res?.message)
          }
        })
        .catch((error) => {
          showErrorToast(error?.message)
        })
        .finally(() => {
          setProcessing(false)
        })
    }
  }

  return (
    <div className="pt-5 px-4">
      <div className="space-y-3 pb-4">
        <Text
          tag="h2"
          variant="headline"
          size="small"
          className="text-[#101828]"
        >
          Please provide details of the patient’s doctor
        </Text>

        <Text tag="p" variant="body" size="medium" className="text-[#475467]">
          We ask your doctor’s details to ensure seamless coordination of your
          care
        </Text>
      </div>

      <div className="flex flex-col gap-5 mt-6 pb-6">
        {/* Doctor's state */}
        <div onClick={() => handleActiveSession('state')}>
          <InputField
            name="state"
            label="Doctor's state"
            type="text"
            placeholder="Select Doctor's State"
            value={selectedState?.state_name}
            readOnly={true}
            rightIcon={
              loading ? <Spinner /> : <ChevronDownIcon className="rotate-90" />
            }
          />
        </div>

        {/* Doctor's city */}
        <div
          onClick={() =>
            selectedState?.state_name && handleActiveSession('city')
          }
        >
          <InputField
            name="city"
            label="Doctor's city"
            type="text"
            placeholder="Select Doctor's City"
            value={selectedCity?.city_name}
            readOnly={true}
            rightIcon={
              loading ? <Spinner /> : <ChevronDownIcon className="rotate-90" />
            }
          />
        </div>

        {/* Hospital */}
        <div
          onClick={() =>
            selectedCity?.city_name && handleActiveSession('hospital')
          }
        >
          <InputField
            name="city"
            label="Hospital"
            type="text"
            placeholder="Select Hospital"
            value={selectedHospital?.clinic_name}
            readOnly={true}
            rightIcon={
              loading ? <Spinner /> : <ChevronDownIcon className="rotate-90" />
            }
          />
        </div>

        {/* Doctor */}
        <div
          onClick={() =>
            selectedHospital?.clinic_name && handleActiveSession('doctor')
          }
        >
          <InputField
            name="city"
            label="Doctor"
            type="text"
            placeholder="Select Doctor"
            value={selectedDoctor?.name}
            readOnly={true}
            rightIcon={
              loading ? <Spinner /> : <ChevronDownIcon className="rotate-90" />
            }
          />
        </div>

        <ProceedButton
          onMoveNext={handleFinish}
          isLastStep={isLastStep}
          disabled={
            !selectedState?.state_name || !selectedCity?.city_name || processing
          }
          canSkip={canSkip}
          onSkip={onSkip}
        />
      </div>

      {/* state list */}
      <BottomSheet
        show={Boolean(activeSection)}
        onClose={handleOnCloseSheet}
        title={
          addDoctor && activeSection === 'doctor'
            ? 'Add Doctor'
            : addHospital && activeSection === 'hospital'
              ? 'Add Hospital'
              : `Select ${activeSection}`
        }
      >
        <div className="min-h-[80vh] max-h-[80vh] px-4 pt-5 space-y-5">
          {!addDoctor && !addHospital && (
            <>
              <div>
                <InputField
                  name="state"
                  type="text"
                  placeholder={`Search your ${activeSection}`}
                  onChange={handleOnSearch}
                  value={searchQuery}
                  disabled={loading}
                  rightIcon={
                    loading ? (
                      <Spinner />
                    ) : (
                      <div className="relative h-full w-full">
                        <SearchIcon />
                      </div>
                    )
                  }
                />
              </div>
              <div
                id={stateScrollId}
                ref={stateScrollRef}
                className="max-h-[67vh] overflow-y-auto px-2 pb-3"
                onScroll={
                  activeSection === 'city' ||
                  activeSection === 'hospital' ||
                  activeSection === 'doctor'
                    ? handleListScroll
                    : undefined
                }
              >
                {loading && list?.length === 0 && (
                  <div className="flex justify-center items-center h-10">
                    <Text
                      tag="p"
                      variant="body"
                      size="large"
                      className="text-[#6C7278]"
                    >
                      Loading...
                    </Text>
                  </div>
                )}
                {activeSection === 'state' ||
                activeSection === 'city' ||
                activeSection === 'hospital' ||
                activeSection === 'doctor' ? (
                  <InfiniteScrollPagination
                    dataLength={list?.length || 0}
                    next={
                      activeSection === 'state'
                        ? loadMoreStates
                        : activeSection === 'city'
                          ? loadMoreCities
                          : activeSection === 'hospital'
                            ? loadMoreHospitals
                            : loadMoreDoctors
                    }
                    hasMore={
                      activeSection === 'state'
                        ? hasMoreStates
                        : activeSection === 'city'
                          ? hasMoreCities
                          : activeSection === 'hospital'
                            ? hasMoreHospitals
                            : hasMoreDoctors
                    }
                    scrollableTarget={stateScrollId}
                    loader={
                      <div className="flex justify-center items-center py-4">
                        <Spinner />
                      </div>
                    }
                  >
                    <ul className="space-y-4">
                      {list?.map((item, index) => (
                        <li
                          key={
                            activeSection === 'state'
                              ? item?.state_master_id ?? index
                              : activeSection === 'city'
                                ? item?.city_master_id ?? index
                                : activeSection === 'hospital'
                                  ? item?.clinic_master_id ?? index
                                  : item?.id ??
                                    item?.doctor_id ??
                                    item?.dr_id ??
                                    index
                          }
                          onClick={() => handleOptionClick(item)}
                        >
                          <Text
                            tag="p"
                            variant="body"
                            size="large"
                            className="text-[#667085]"
                          >
                            {activeSection === 'state'
                              ? item?.state_name
                              : activeSection === 'city'
                                ? item?.city_name
                                : activeSection === 'hospital'
                                  ? item?.clinic_name
                                  : item?.name}
                          </Text>
                        </li>
                      ))}
                    </ul>
                  </InfiniteScrollPagination>
                ) : (
                  <ul className="space-y-4">
                    {list?.map((item, index) => (
                      <li key={index} onClick={() => handleOptionClick(item)}>
                        <Text
                          tag="p"
                          variant="body"
                          size="large"
                          className="text-[#667085]"
                        >
                          {activeSection === 'state'
                            ? item?.state_name
                            : activeSection === 'city'
                              ? item?.city_name
                              : activeSection === 'hospital'
                                ? item?.clinic_name
                                : item?.name}
                        </Text>
                      </li>
                    ))}
                  </ul>
                )}
                {list?.length === 0 && !loading && (
                  <div className="flex h-10">
                    <Text
                      tag="p"
                      variant="body"
                      size="large"
                      className="text-[#6C7278]"
                    >
                      No data found
                    </Text>
                  </div>
                )}

                {!loading && activeSection === 'doctor' && (
                  <div className="mt-5">
                    <button
                      className="flex items-center gap-1"
                      onClick={() => setAddDoctor(true)}
                    >
                      <Text
                        tag="p"
                        variant="body"
                        size="large"
                        className="underline text-[#299D6B]"
                      >
                        Add Doctor
                      </Text>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="11"
                        height="9"
                        viewBox="0 0 11 9"
                        fill="none"
                      >
                        <path
                          d="M6.31423 0.247648C6.32256 0.239277 6.57783 0.175271 6.88146 0.105463C7.18513 0.0356547 7.43686 -0.010793 7.44091 0.00217185C7.44492 0.0151757 7.47634 0.226001 7.51069 0.470699C7.57971 0.96208 7.74681 1.53581 7.92536 1.89462C8.48192 3.01287 9.48048 3.75331 10.6916 3.94576L11 3.99477L11 4.48876L11 4.98279L10.7205 5.02971C8.88911 5.33705 7.69514 6.70681 7.47179 8.75666C7.45722 8.89052 7.43979 9 7.43308 9C7.37131 9 6.34789 8.75328 6.32853 8.73373C6.31454 8.7196 6.32495 8.59412 6.3517 8.45493C6.64785 6.91367 7.49581 5.6595 8.59619 5.1351L8.86015 5.00931L3.43225 4.99945L-4.3652e-07 4.9932L-3.49118e-07 3.99344L3.44108 3.98718L8.85618 3.97729L8.52294 3.8084C7.65844 3.37028 6.974 2.51086 6.57093 1.35729C6.45608 1.02866 6.28277 0.279418 6.31423 0.247648Z"
                          fill="#299D6B"
                        />
                      </svg>
                    </button>
                  </div>
                )}
                {!loading && activeSection === 'hospital' && (
                  <div className="mt-5">
                    <button
                      className="flex items-center gap-1"
                      onClick={() => setAddHospital(true)}
                    >
                      <Text
                        tag="p"
                        variant="body"
                        size="large"
                        className="underline text-[#299D6B]"
                      >
                        Add Hospital
                      </Text>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="11"
                        height="9"
                        viewBox="0 0 11 9"
                        fill="none"
                      >
                        <path
                          d="M6.31423 0.247648C6.32256 0.239277 6.57783 0.175271 6.88146 0.105463C7.18513 0.0356547 7.43686 -0.010793 7.44091 0.00217185C7.44492 0.0151757 7.47634 0.226001 7.51069 0.470699C7.57971 0.96208 7.74681 1.53581 7.92536 1.89462C8.48192 3.01287 9.48048 3.75331 10.6916 3.94576L11 3.99477L11 4.48876L11 4.98279L10.7205 5.02971C8.88911 5.33705 7.69514 6.70681 7.47179 8.75666C7.45722 8.89052 7.43979 9 7.43308 9C7.37131 9 6.34789 8.75328 6.32853 8.73373C6.31454 8.7196 6.32495 8.59412 6.3517 8.45493C6.64785 6.91367 7.49581 5.6595 8.59619 5.1351L8.86015 5.00931L3.43225 4.99945L-4.3652e-07 4.9932L-3.49118e-07 3.99344L3.44108 3.98718L8.85618 3.97729L8.52294 3.8084C7.65844 3.37028 6.974 2.51086 6.57093 1.35729C6.45608 1.02866 6.28277 0.279418 6.31423 0.247648Z"
                          fill="#299D6B"
                        />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
          {/* Add Doctor Form */}
          {addDoctor && activeSection === 'doctor' && (
            <div className="">
              <InputField
                name="doctor"
                label="Doctor"
                type="text"
                placeholder="Enter Doctor's Name"
                value={doctorName}
                onChange={(e) => setDoctorName(e.target.value)}
              />

              <ProceedButton
                onMoveNext={handleAddDoctor}
                btnText="Add Doctor"
                disabled={!doctorName || addDoctorLoading}
              />
            </div>
          )}
          {/* Add Hospital Form */}
          {addHospital && activeSection === 'hospital' && (
            <div>
              <InputField
                name="hospital"
                label="Hospital"
                type="text"
                placeholder="Enter Hospital Name"
                value={hospitalName}
                onChange={(e) => {
                  console.log(
                    'hospitalName',
                    e.target.value,
                    addHospitalLoading,
                  )
                  setHospitalName(e.target.value)
                }}
              />
              <ProceedButton
                onMoveNext={handleAddHospital}
                btnText="Add Hospital"
                disabled={!hospitalName || addHospitalLoading}
              />
            </div>
          )}
        </div>
      </BottomSheet>
    </div>
  )
}

export default DoctorLocation
